import { Link } from '@tanstack/react-router';
import { AvatarDisc, Button, LoadingState, MonoLabel, Row, SectionHeader, Star } from '@/component';
import { useFavourites, useFriendActions, useFriendFeed, useFriendList, useFriendSuggestions, type Suggestion } from '@/hook';
import { formatDayLabel } from '@/lib/time';
import { FriendSearch } from './FriendSearch';
import { personName } from './personName';

const FEED_ROWS = 5;

/** "@rosam · The Hideaway · Friday": the handle, then the room and night
 * shared, under the "Sang with you" heading. The room is whatever the
 * mobile app could name, the venue first. */
export function suggestionReason(person: Suggestion): string {
  return [person.handle && `@${person.handle}`, person.sangWith.venue ?? person.sangWith.systemName, formatDayLabel(person.sangWith.sangAt)]
    .filter(Boolean)
    .join(' · ');
}

/** Home's Friends column: the handle search, then what friends sang lately,
 * or, with no friends yet, the people who have been in a session with you. */
export function HomeFriends() {
  const list = useFriendList();
  const feed = useFriendFeed(FEED_ROWS);
  const suggestions = useFriendSuggestions();
  const { favourites, toggle } = useFavourites();
  const { request, dismiss } = useFriendActions();
  const friendCount = list?.friends.length ?? 0;

  return (
    <section>
      <SectionHeader
        title="Friends"
        action={
          <Link to="/friends">
            <MonoLabel tone="mute">{friendCount > 0 ? `All ${friendCount} →` : 'Friends →'}</MonoLabel>
          </Link>
        }
      />
      <FriendSearch size="sm" className="mt-4" />
      {(list === undefined || feed === undefined) && <LoadingState />}
      {list && friendCount > 0 && feed && (
        <div>
          {feed.length === 0 && <p className="py-6 text-sm text-mute">Your friends have not sung yet. It shows here when they do.</p>}
          {feed.map((item) => (
            <Row key={item.historyId} className="items-start gap-[14px] py-4">
              {item.singer.handle ? (
                <Link to="/u/$handle" params={{ handle: item.singer.handle }} aria-label={`${personName(item.singer)}'s profile`}>
                  <AvatarDisc user={item.singer} size={32} />
                </Link>
              ) : (
                <AvatarDisc user={item.singer} size={32} />
              )}
              <div className="min-w-0 flex-1 text-sm leading-[1.45]">
                <span className="font-semibold">{personName(item.singer)}</span> sang{' '}
                <Link to="/tracks/$trackId" params={{ trackId: item.trackId }} className="font-semibold hover:text-acc">
                  {item.title}
                </Link>
                <MonoLabel className="mt-[5px] block tracking-[0.1em]">
                  {[formatDayLabel(item.sangAt), item.venue].filter(Boolean).join(' · ')}
                </MonoLabel>
              </div>
              <Star on={favourites.has(item.trackId)} onToggle={() => toggle(item.trackId)} />
            </Row>
          ))}
        </div>
      )}
      {list && friendCount === 0 && (
        <div className="pb-1 pt-7">
          <p className="max-w-[38ch] text-[15px] leading-[1.5] [text-wrap:pretty]">
            No friends yet. Add someone and you will see what they sing.
          </p>
          {suggestions && suggestions.length > 0 && (
            <>
              <MonoLabel className="mt-6 block border-b border-line pb-[10px]">Sang with you</MonoLabel>
              {suggestions.map((person) => (
                <Row key={person.userId} className="gap-[14px] py-[14px]">
                  <AvatarDisc user={person} size={32} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">{personName(person)}</div>
                    <MonoLabel className="mt-0.5 block truncate normal-case tracking-normal text-[11px]">{suggestionReason(person)}</MonoLabel>
                  </div>
                  <Button size="sm" className="px-[13px] py-[7px] text-xs" aria-label={`Add ${personName(person)}`} onClick={() => void request(person.userId)}>
                    Add
                  </Button>
                  <Button
                    variant="text"
                    size="sm"
                    quiet
                    aria-label={`Dismiss ${personName(person)}`}
                    className="text-[17px] leading-none"
                    onClick={() => void dismiss(person.userId)}
                  >
                    ×
                  </Button>
                </Row>
              ))}
            </>
          )}
        </div>
      )}
    </section>
  );
}
